<template>
  <div class="w-full h-full">
    <AlertDialog :open="showFormModal" @update:open="(o) => !o && (showFormModal = false)">
      <AlertDialogContent class="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Request approval</AlertDialogTitle>
        </AlertDialogHeader>
        <div>
          <p class="mb-2">Set default splits, for cashback if not preset</p>
          <label class="grid gap-1 text-sm mb-2">
            <span>Your users cashback split percentage</span>
            <Input v-model="activateOfferRequest.splitOfCashbackPercentage.endUser" type="number" :min="0" :max="100" />
            <span v-if="pecentageNotRight" class="text-xs text-destructive">{{ pecentageNotRight }}</span>
          </label>
          <label class="grid gap-1 text-sm">
            <span>Your commission split percentage</span>
            <Input v-model="activateOfferRequest.splitOfCashbackPercentage.client" type="number" :min="0" :max="100" :disabled="true" />
          </label>
        </div>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="showFormModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            :disabled="activateOfferRequest.splitOfCashbackPercentage.client > 100 || activateOfferRequest.splitOfCashbackPercentage.client < 0"
            @click="() => { showFormModal = false; requestApproval() }">Request</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Card>
      <CardHeader>
        <CardTitle>Integrated offers catalogue</CardTitle>
        <CardDescription>View pre-integrated offers avaliable across our network</CardDescription>
      </CardHeader>
      <CardContent class="space-y-4">
        <DataTable :headers="headersTable1" :items="retailers" :loading="loading"
          v-model:server-options="serverOptions" :server-items-length="serverItemsLength"
          :error="error"
          @retry="load(serverOptions.page - 1, serverOptions.rowsPerPage)"
          searchable
          search-placeholder="Search by retailer name"
          :search="retailerSearchInput"
          @update:search="retailerSearchInput = $event"
          @search="getRetailerBySearch(0, serverOptions.rowsPerPage, retailerSearchInput)"
          @clear-search="onClearSearch()"
          :filter-groups="offerFilterGroups"
          :filters="offerFilters"
          @filter-change="onOfferFilterChange">
          <template #toolbar-actions>
            <Tabs v-model="activeTab" @update:model-value="onTabChange">
              <TabsList class="flex-wrap">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="newlyAdded">Newly added</TabsTrigger>
                <TabsTrigger value="approved">Approved</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="declined">Declined</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select v-model="orderBy" @update:model-value="onSortChange">
              <SelectTrigger size="sm" class="w-40" aria-label="Order by">
                <SelectValue placeholder="Order by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in orderByList" :key="o.id" :value="o.id">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </template>
          <template #item-retailer="retailers">
            <div class="flex justify-start items-center gap-2.5">
              <img class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white" alt=""
                :src="retailers.retailerOffer.displayInformation.logo" />
              <div class="min-w-0">
                <p class="text-sm font-medium truncate" :title="retailers.retailerOffer.retailer.retailerName">{{ retailers.retailerOffer.retailer.retailerName }}</p>
                <div class="mt-1 flex gap-1 items-center">
                  <Badge variant="outline">{{ formatOfferType(retailers.retailerOffer.offerType) }}</Badge>
                  <Badge variant="outline">{{ retailers.retailerOffer.country }}</Badge>
                </div>
                <CopyId class="mt-1" :value="retailers.retailerOffer.id" :max-width="120" />
              </div>
            </div>
          </template>

          <template #item-cashbackAmount="retailers">
            <span class="text-sm tabular-nums whitespace-nowrap">{{ cashbackHeadline(retailers.retailerOffer) }}</span>
            <p class="text-xs text-muted-foreground mt-1">{{ cashbackCap(retailers.retailerOffer) }}</p>
          </template>

          <template #item-status="retailer">
            <span class="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
              <component
                :is="approvalIcon(retailer.offerApproval)"
                :class="[
                  'size-3.5',
                  retailer.offerApproval === 'APPROVED' ? 'text-green-500 dark:text-green-400' : '',
                  retailer.offerApproval === 'DECLINED' ? 'text-red-500 dark:text-red-400' : '',
                  retailer.offerApproval === 'PENDING' ? 'animate-spin' : '',
                ]"
              />
              {{ approvalLabel(retailer.offerApproval) }}
            </span>
          </template>

          <template #item-supplier="retailers">
            <span class="text-xs font-mono whitespace-nowrap">{{ retailers.retailerOffer.supplier }}</span>
          </template>

          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button variant="outline" size="sm"
                @click="$router.push(`/retailer/${retailers.retailerOffer.retailer.id}/offer/${retailers.retailerOffer.id}`)">View Offer</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Input,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  CopyId,
  Tabs,
  TabsList,
  TabsTrigger,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@boogi/ui'
import { CircleCheck, Ban, Loader, CircleDashed } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    Input,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    CopyId,
    Tabs,
    TabsList,
    TabsTrigger,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    CircleCheck,
    Ban,
    Loader,
    CircleDashed,
  },
  props: {},

  data() {
    return {
      retailerSearchInput: '',
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      showFilters: false,
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      pecentageNotRight: '',

      activateOfferRequest: {
        splitOfCashbackPercentage: {
          endUser: 50,
          client: 50,
        },
        activate: true,
      },

      selectedRetailer: undefined,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Retailer', value: 'retailer', width: 400 },
        { text: 'Cashback amount', value: 'cashbackAmount' },
        { text: 'Approval status', value: 'status' },
        { text: 'Supplier', value: 'supplier' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],
      orderByList: [
        { id: 'MOSTPOPULAR', text: 'Most Popular' },
        { id: 'ATOZ', text: 'A to Z' },
        { id: 'ZTOA', text: 'Z to A' },
      ],
      offerTypes: [
        { id: 'PAYMENT', text: 'Payment-linked offers' },
        { id: 'ONLINE', text: 'Affiliate links' },
        { id: 'CLICK_TO_ACTIVATE', text: 'Click to activate' },
      ],
      approvalStatuses: [
        { id: 'APPROVED', text: 'Approved' },
        { id: 'PENDING', text: 'Pending' },
        { id: 'DECLINED', text: 'Declined' },
        { id: 'NOT_APPLIED_FOR', text: 'Not applied for' },
      ],
      activeFilters: [
        { id: 'PAYMENT', filter: true },
        { id: 'ONLINE', filter: true },
        { id: 'GIFTCARD', filter: true },
        { id: 'CLICK_TO_ACTIVATE', text: 'Click to activate' },
        { id: 'Category', filterText: 'Restaurant' },
      ],
      offerType: [],
      approvalStatus: [],
      orderBy: 'MOSTPOPULAR',
      activeTab: 'all',
      selectedCountry: [],
      selectedSupplier: [],
      countryOptions: [],
      supplierOptions: [],
    }
  },
  created() {
    this.getRetailers(0, 25)
    this.getFilterOptions()
  },
  methods: {
    cashbackValue(offer) {
      return (
        (offer &&
          offer.cashbackRules &&
          offer.cashbackRules[0] &&
          offer.cashbackRules[0].conditions &&
          offer.cashbackRules[0].conditions[0] &&
          offer.cashbackRules[0].conditions[0].value) ||
        {}
      )
    },
    // Rate is a percentage for PERCENTAGE / PERCENTAGE_MAX ("X% up to £Y"), a £ amount
    // only for FIXED. The old inline check missed PERCENTAGE_MAX and showed its rate as £.
    cashbackHeadline(offer) {
      const value = this.cashbackValue(offer)
      if (value.maxAvailableValue == null) return '—'
      const isPercentage = value.type === 'PERCENTAGE' || value.type === 'PERCENTAGE_MAX'
      const currency = (offer && offer.currencyList && offer.currencyList[0]) || ''
      return `${isPercentage ? '' : currency}${value.maxAvailableValue}${isPercentage ? '%' : ''}`
    },
    cashbackCap(offer) {
      const value = this.cashbackValue(offer)
      if (value.type === 'PERCENTAGE_MAX' && value.maximum > 0) {
        return `Up to £${value.maximum} per transaction`
      }
      if (offer && (offer.spendingCapped || offer.spendingCapAmount > 0)) {
        return `Cashback capped at £${offer.spendingCapAmount} per user`
      }
      return 'No cap applied'
    },
    formatOfferType(type) {
      if (type === 'PAYMENT') return 'Payment-linked'
      if (type === 'ONLINE') return 'Affiliate links'
      if (type === 'CLICK_TO_ACTIVATE') return 'Click to activate'
      return type
    },

    approvalLabel(status) {
      if (status === 'APPROVED') return 'Approved'
      if (status === 'DECLINED') return 'Declined'
      if (status === 'PENDING') return 'Pending'
      return 'Not applied for'
    },

    approvalIcon(status) {
      if (status === 'APPROVED') return CircleCheck
      if (status === 'DECLINED') return Ban
      if (status === 'PENDING') return Loader
      return CircleDashed
    },

    async getFilterOptions() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/filter-options`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `${token}` },
          }
        )
        if (response.ok) {
          const object = await response.json()
          this.countryOptions = object.countries || []
          this.supplierOptions = object.suppliers || []
        }
      } catch (e) {
        return
      }
    },

    resetToFirstPage() {
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
    },

    // Shared query fragment for approval (tab), sort (tab/dropdown), country and supplier.
    catalogueParams() {
      const type = this.offerType.map((o) => o.id)
      const approval = this.approvalForTab
      return (
        `${type.length > 0 ? '&type=' + type : ''}` +
        `${approval.length > 0 ? '&approval=' + approval : ''}` +
        `&sort=${this.sortForTab}` +
        `${this.selectedCountry.length > 0 ? '&country=' + this.selectedCountry : ''}` +
        `${this.selectedSupplier.length > 0 ? '&supplier=' + this.selectedSupplier : ''}`
      )
    },

    load(page, size) {
      if (this.retailerSearchInput) {
        return this.getRetailerBySearch(page, size, this.retailerSearchInput)
      }
      return this.getRetailers(page, size)
    },

    onTabChange(tab) {
      this.activeTab = tab
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    onSortChange() {
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    onClearSearch() {
      this.retailerSearchInput = ''
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    onOfferFilterChange(updated) {
      const typeIds = (updated && updated.offerType) || []
      this.offerType = this.offerTypes.filter((o) => typeIds.includes(o.id))
      this.selectedCountry = (updated && updated.country) || []
      this.selectedSupplier = (updated && updated.supplier) || []
      this.resetToFirstPage()
      this.load(0, this.serverOptions.rowsPerPage)
    },

    async getRetailerBySearch(page, size, retailerSearch) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/offer-search?search=${retailerSearch}&page=${page}&size=${size}${this.catalogueParams()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.retailers = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async getRetailers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/catalogue/offer?page=${page}&size=${size}${this.catalogueParams()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    offerFilterGroups() {
      return [
        {
          key: 'offerType',
          label: 'Offer types',
          options: this.offerTypes.map((o) => ({ value: o.id, label: o.text })),
        },
        {
          key: 'country',
          label: 'Country',
          options: this.countryOptions.map((c) => ({ value: c, label: c })),
        },
        {
          key: 'supplier',
          label: 'Supplier',
          options: this.supplierOptions.map((s) => ({ value: s, label: s })),
        },
      ]
    },
    offerFilters() {
      return {
        offerType: this.offerType.map((o) => o.id),
        country: this.selectedCountry,
        supplier: this.selectedSupplier,
      }
    },
    // Approval statuses to request based on the active tab (empty = no approval filter).
    approvalForTab() {
      const map = { approved: 'APPROVED', pending: 'PENDING', declined: 'DECLINED' }
      return map[this.activeTab] ? [map[this.activeTab]] : []
    },
    // "Newly added" forces newest-first; other tabs use the order-by dropdown.
    sortForTab() {
      return this.activeTab === 'newlyAdded' ? 'LATEST' : this.orderBy
    },
  },
  mounted() {
    document.title = 'Dashboard | Retailer catalogue'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.load(val.page - 1, val.rowsPerPage)
      },
    },
    activateOfferRequest: {
      deep: true,
      handler: function (newValue) {
        if (newValue.splitOfCashbackPercentage.endUser == '') {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
        }

        if (newValue.splitOfCashbackPercentage.endUser > 100) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 100
          this.pecentageNotRight = 'The percentage needs to be less than or equal to 100'
        } else if (newValue.splitOfCashbackPercentage.endUser < 0) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
          this.pecentageNotRight = 'The percentage needs to be greater than or equal to 0'
        }
        this.pecentageNotRight = ''
        this.activateOfferRequest.splitOfCashbackPercentage.client =
          100 - newValue.splitOfCashbackPercentage.endUser
      },
    },
  },
}
</script>
